import { useEffect, useRef, useState } from "react";

/**
 * Drives a polite live region so that a *repeated* message still announces.
 *
 * A live region only fires when its text actually changes, so filtering from one
 * set of 3 results to a different set of 3 renders byte-identical text and stays
 * silent — the user gets no feedback that the list changed (WCAG 4.1.3). Clearing
 * first and writing after a delay forces the change: the React counterpart of
 * announceTabStatus() in Scripts/AdminShared/EmailSMSPageMain.js (AB#9316).
 *
 * The same delay debounces a burst of keystrokes so the queue is not thrashed.
 *
 * Render the returned value into a region that is present and EMPTY on first
 * paint — a region inserted already holding text never announces (AB#9515).
 *
 * @param message text to announce; "" clears the region
 * @param trigger changes whenever a fresh announcement is wanted, even when
 *                `message` is identical to the previous one
 * @param delayMs debounce before the message is written
 */
export function useLiveAnnouncement(
  message: string,
  trigger: string,
  delayMs = 500
): string {
  const [announced, setAnnounced] = useState("");
  const messageRef = useRef(message);

  // Declared first so the announce effect below reads the current message.
  useEffect(() => {
    messageRef.current = message;
  }, [message]);

  useEffect(() => {
    // Clear first, so an identical message is still a DOM change.
    setAnnounced("");
    if (!messageRef.current) return undefined;
    const timer = window.setTimeout(
      () => setAnnounced(messageRef.current),
      delayMs
    );
    return () => window.clearTimeout(timer);
  }, [trigger, delayMs]);

  return announced;
}

export default useLiveAnnouncement;
